import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { formatDateTime } from '@/lib/utils';
import { formatAddress } from '@/lib/address';
import {
  exportCsv,
  formatDuration,
  knotsToKmh,
  metersToKm,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';

export function TripsReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('multi');

  const query = useQuery({
    queryKey: ['report', 'trips', executed],
    queryFn: () =>
      reportsApi.trips({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null,
  });

  const rows = useMemo(
    () =>
      [...(query.data ?? [])].sort(
        (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
      ),
    [query.data],
  );

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `viagens_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      [
        'Máquina',
        'Início',
        'Fim',
        'Duração',
        'Distância (km)',
        'Endereço inicial',
        'Endereço final',
      ],
      rows.map((r) => [
        r.deviceName ?? devices.get(r.deviceId)?.name ?? '',
        formatDateTime(r.startTime),
        formatDateTime(r.endTime),
        formatDuration(r.duration),
        (r.distance / 1000).toFixed(2),
        formatAddress(r.startAddress),
        formatAddress(r.endAddress),
      ]),
    );
  };

  return (
    <ReportShell title="Viagens" description="Trajetos detectados pelo sistema no período.">
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit="viagens"
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Máquina</th>
              <th className="px-4 py-2 font-semibold">Início</th>
              <th className="px-4 py-2 font-semibold">Fim</th>
              <th className="px-4 py-2 font-semibold">Duração</th>
              <th className="px-4 py-2 font-semibold">Distância</th>
              <th className="px-4 py-2 font-semibold">Vel. máx.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.deviceId}-${i}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(r.startTime)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(r.endTime)}</td>
                <td className="px-4 py-2">{formatDuration(r.duration)}</td>
                <td className="px-4 py-2">{metersToKm(r.distance)}</td>
                <td className="px-4 py-2">{knotsToKmh(r.maxSpeed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTable>
    </ReportShell>
  );
}
